import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Checkbox, FlexColumn, Text} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import Util from '../../../shared/util/util';
import UpdateProjectPortalConfigMutation from '../../../../mutations/update_project_portal_config_mutation';

/**
 * FOR-48 (BU-10) 10b — Project Settings → Client Portal tab. The ONLY project-level
 * portal controls: "Show in client portal" (publishes this project to the client's
 * portal) + phase-status visibility. Access configuration (activation, branding,
 * feature flags) is Client Organisation level (10a), NOT here.
 *
 * Writes go through the portal proxy mutation keyed by the project's PSA id
 * (companyProjectId — the identifier the PSA feed syncs as psa_project_id). No
 * forecast_db write.
 */
const ProjectClientPortal = ({psaProjectId, portalConfig}) => {
	const {formatMessage} = useIntl();
	const [showInClientPortal, setShowInClientPortal] = useState(!!portalConfig?.showInClientPortal);
	const [phaseStatusVisible, setPhaseStatusVisible] = useState(
		portalConfig?.portalPhaseStatusVisible == null ? true : !!portalConfig.portalPhaseStatusVisible
	);

	const save = next => {
		Util.CommitMutation(UpdateProjectPortalConfigMutation, {
			psaProjectId: String(psaProjectId),
			showInClientPortal: next.showInClientPortal,
			portalPhaseStatusVisible: next.phaseStatusVisible,
		});
	};

	const onToggleShow = () => {
		const value = !showInClientPortal;
		setShowInClientPortal(value);
		save({showInClientPortal: value, phaseStatusVisible});
	};

	const onTogglePhaseStatus = () => {
		const value = !phaseStatusVisible;
		setPhaseStatusVisible(value);
		save({showInClientPortal, phaseStatusVisible: value});
	};

	return (
		<FlexColumn gap={'l'} data-cy="project-client-portal-tab">
			{/* Checkbox renders `labelText`, not children — children passed to it are
			    dropped, leaving an unlabelled box. */}
			<Checkbox
				checked={showInClientPortal}
				onChange={onToggleShow}
				data-cy="show-in-client-portal-toggle"
				labelText={formatMessage({id: 'client_portal.show_in_portal', defaultMessage: 'Show in client workspace'})}
			/>
			<Text color={'secondary'}>
				<FormattedMessage
					id="client_portal.show_in_portal_help"
					defaultMessage="Publishes this project to the client's workspace. Does not affect sibling projects or the client's workspace activation."
				/>
			</Text>
			<Checkbox
				checked={phaseStatusVisible}
				onChange={onTogglePhaseStatus}
				data-cy="phase-status-visible-toggle"
				labelText={formatMessage({
					id: 'client_portal.phase_status_visible',
					defaultMessage: 'Show phase status in workspace',
				})}
			/>
		</FlexColumn>
	);
};

ProjectClientPortal.propTypes = {
	psaProjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	portalConfig: PropTypes.shape({
		showInClientPortal: PropTypes.bool,
		portalPhaseStatusVisible: PropTypes.bool,
	}),
};

export default ProjectClientPortal;
